// Artifactory
import {evaluateExperimentAndLog} from '@homeaway/abacus-client-utils';
import {localizeUrl} from '@homeaway/localization-utils';

// Relative Imports
import triggerEdap from '../api/edap';
import edapActions from '../actions/edapActions';
import getPricingInfoFromState from '../middleware/utils/getPricingInfoFromState';
import {getPriceQuotePresentedData} from '../selectors/rightRail';
import {shouldTriggerPriceQuotePresented} from './utils/shouldTriggerPriceQuotePresented';
import {
    getCheckoutSelectedData,
    getPropertySummaryPresented,
    getPropertyDetailsData,
    getUxPropertyDetailsNavigationSelectedData
} from '../selectors/edapEvents';

const EVENT_CATEGORY = 'pdp page';
const BOOKING_BUTTON_CLICK = 'booking button click';
const LISTING_BOOKNOW_CLICK = 'listing.booknow.click';
const CHECKOUT_SELECTED = 'checkout.selected';
const PROPERTY_SUMMARY_PRESENTED = 'property_summary.presented';
const PRICE_QUOTE_PRESENTED = 'price_quote.presented';
const PROPERTY_DETAILS_MAP_PRESENTED = 'property_details_map.presented';
const PROPERTY_DETAILS_PARTNER_INFORMATION_DETAILS_SELECTED =
    'property_details_partner_information_details.selected';
const UX_PROPERTY_DETAILS_NAVIGATION_SELECTED = 'ux.property_details_navigation.selected';

let loggedPageview = false;
const queuedEvents = [];

/* istanbul ignore next */
function triggerEdapEvent(store, action = {payload: {eventName: '', eventData: '', options: {}}}) {
    const {payload} = action;

    // queue up all non-pageview events to be logged *after* the pageview event has been fired
    // (NOTE: we're doing this to ensure that the `pageview` event is logged before all other application events, which is a requirement
    // if we want to segment on PDP pageviews in ART readouts while looking at app metrics like `quote.attempt`)
    if (!loggedPageview && payload.eventName !== 'pageview') {
        queuedEvents.push(payload);
        return;
    }

    // this is either the pageview event or the pageview event has already been logged, so go ahead and log the event
    triggerEdap.call(window, payload.eventName, payload.eventData, payload.options);

    // check if we just logged the pageview event and flush all queued events if necessary
    if (payload.eventName === 'pageview') {
        loggedPageview = true;

        // 'ha_pdp_bizmetrics' is an AA test used solely to view edap metrics in datadog
        // We want to log it after pageview event.
        evaluateExperimentAndLog('ha_pdp_bizmetrics');

        queuedEvents.forEach((queuedEvent) => {
            triggerEdap.call(
                window,
                queuedEvent.eventName,
                queuedEvent.eventData,
                queuedEvent.options
            );
        });
    }
}

function getFullBookingUrl(listingObj) {
    // extract and format the checkout URL
    return listingObj.checkoutUrl
        ? localizeUrl(listingObj.checkoutUrl, window.__SITE_CONTEXT__, {absolute: true})
        : null;
}

// Trigger an event that looks like
// 'listing.booknow.click', {
//     href: fullBookingUrl,
//     ...
// }
function triggerEdapListingBooknowClick(store, action = {payload: {}}) {
    const state = store.getState();

    // get trip data
    const tripData = state.tripDetailsReducer;
    const eventArrival = tripData.arriveDateText;
    const eventDeparture = tripData.departDateText;
    const eventNumAdults = tripData.adults.toString();
    const eventNumChildren = tripData.children.toString();
    const eventHasPets = tripData.hasPets.toString();
    const eventActionLocation = action.payload.actionLocation;

    // get pricing + checkout data
    const pricingInfoObjJs = getPricingInfoFromState(state);
    const fullBookingUrl = getFullBookingUrl(pricingInfoObjJs);
    const eventTripTotal = pricingInfoObjJs.tripTotal;

    // populate edap event with trip details
    store.dispatch(
        edapActions.triggerEdapEvent(
            LISTING_BOOKNOW_CLICK,
            {
                href: fullBookingUrl,
                eventstartdate: eventArrival,
                eventenddate: eventDeparture,
                eventnumadults: eventNumAdults,
                eventnumchildren: eventNumChildren,
                eventpets: eventHasPets,
                triptotal: eventTripTotal,
                actionlocation: eventActionLocation,
                eventcategory: EVENT_CATEGORY,
                eventaction: BOOKING_BUTTON_CLICK
            },
            {
                delayProcessing: true
            }
        )
    );
}

function triggerEdapCheckoutSelected(store) {
    const state = store.getState();
    const data = getCheckoutSelectedData(state);
    store.dispatch(
        edapActions.triggerEdapEvent(CHECKOUT_SELECTED, {
            action_location: 'property',
            ...data
        })
    );
}

const getBadgesName = (badges) => {
    let names;
    if (!!badges && !!badges.length) {
        names = badges.map(({name}) => name);
    }
    return (names || []).join(',');
};

function triggerEdapPropertySummaryPresented(store) {
    const state = store.getState();
    const data = getPropertySummaryPresented(state);
    const {
        adultCount,
        childCount,
        dateEnd,
        dateStart,
        listingId,
        hasPets,
        rankedBadges,
        nldBadge,
        superlativeBadge
    } = data;
    const badges = [
        getBadgesName(rankedBadges),
        getBadgesName(nldBadge),
        getBadgesName(superlativeBadge)
    ]
        .filter(Boolean)
        .join(',');
    store.dispatch(
        edapActions.triggerEdapEvent(PROPERTY_SUMMARY_PRESENTED, {
            action_location: 'property',
            adult_count: adultCount.toString(),
            child_count: childCount.toString(),
            date_end: dateEnd,
            date_start: dateStart,
            listing_id: listingId,
            with_pets: `${hasPets}`,
            badges
        })
    );
}

const triggerEdapPriceQuotePresented = (store) => {
    const state = store.getState();

    const data = getPriceQuotePresentedData(state);
    const {
        adultsCount,
        arriveDateText,
        departDateText,
        edapPriceQuotePresented,
        loadingRate,
        requestToBookModalOpen,
        hasTotals
    } = data;

    if (
        requestToBookModalOpen ||
        !edapPriceQuotePresented ||
        loadingRate ||
        !hasTotals ||
        !arriveDateText ||
        !departDateText ||
        adultsCount <= 0
    ) {
        return;
    }
    const result = shouldTriggerPriceQuotePresented(edapPriceQuotePresented);

    if (result) {
        store.dispatch(
            edapActions.triggerEdapEvent(PRICE_QUOTE_PRESENTED, {
                action_location: 'pdp',
                ...result
            })
        );
    }
};

const uxPropertyDetailsNavigationSelected = (store, action = {payload: {}}) => {
    const {tabName, actionLocation} = action.payload;
    const state = store.getState();
    const data = getUxPropertyDetailsNavigationSelectedData(state);
    store.dispatch(
        edapActions.triggerEdapEvent(UX_PROPERTY_DETAILS_NAVIGATION_SELECTED, {
            action_location: actionLocation || 'property',
            property_details_section: tabName,
            ...data
        })
    );
};

function logTestExposure(store, action = {payload: {}}) {
    const {testName} = action.payload;
    evaluateExperimentAndLog(testName);
}

const triggerEdapPropertyDetailsMapPresented = (store) => {
    const state = store.getState();
    const data = getPropertyDetailsData(state);
    store.dispatch(
        edapActions.triggerEdapEvent(PROPERTY_DETAILS_MAP_PRESENTED, {
            action_location: 'property',
            property_details_section: 'map',
            ...data
        })
    );
};

const triggerEdapPropertyDetailsPartnerInformationDetailsSelected = (store) => {
    const state = store.getState();
    const data = getPropertyDetailsData(state);
    store.dispatch(
        edapActions.triggerEdapEvent(PROPERTY_DETAILS_PARTNER_INFORMATION_DETAILS_SELECTED, {
            action_location: 'property',
            ...data
        })
    );
};

export default triggerEdapEvent;
export {
    logTestExposure,
    triggerEdapEvent,
    triggerEdapListingBooknowClick,
    triggerEdapCheckoutSelected,
    triggerEdapPropertySummaryPresented,
    getFullBookingUrl,
    triggerEdapPropertyDetailsPartnerInformationDetailsSelected,
    triggerEdapPriceQuotePresented,
    getBadgesName,
    triggerEdapPropertyDetailsMapPresented,
    uxPropertyDetailsNavigationSelected,
    EVENT_CATEGORY,
    BOOKING_BUTTON_CLICK,
    LISTING_BOOKNOW_CLICK,
    CHECKOUT_SELECTED,
    PROPERTY_SUMMARY_PRESENTED,
    PRICE_QUOTE_PRESENTED,
    PROPERTY_DETAILS_PARTNER_INFORMATION_DETAILS_SELECTED,
    UX_PROPERTY_DETAILS_NAVIGATION_SELECTED,
    PROPERTY_DETAILS_MAP_PRESENTED
};
